import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Chip } from '@mui/material';
import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExampleLetterModal from './ExampleLetterModal';

const exampleLetters = [
  {
    id: 1,
    company: 'Amazon',
    companyIcon: '📦',
    headline: 'Got $187.42 back from Amazon',
    amount: 187.42,
    daysToRefund: 4,
    preview: `Dear Amazon Customer Service,

Re: Formal Dispute of Duplicate Charge – Order #114-9876543-9876543

I am writing to dispute an unauthorized duplicate charge of $187.42 that appeared on my account on November 12, 2025. My bank statement shows two identical charges from Amazon.com for the same order on the same day.

Under the Fair Credit Billing Act (15 U.S.C. § 1666), I have the right to dispute billing errors, including duplicate charges. This statute requires merchants to investigate and resolve billing disputes within two billing cycles...`,
    fullLetter: `Dear Amazon Customer Service,

Re: Formal Dispute of Duplicate Charge – Order #114-9876543-9876543

I am writing to dispute an unauthorized duplicate charge of $187.42 that appeared on my account on November 12, 2025. My bank statement shows two identical charges from Amazon.com for the same order on the same day.

LEGAL BASIS FOR DISPUTE:

Under the Fair Credit Billing Act (15 U.S.C. § 1666), I have the right to dispute billing errors, including duplicate charges. This statute requires merchants to investigate and resolve billing disputes within two billing cycles (but not more than 90 days). Section 1666(a)(3) specifically defines "billing errors" to include charges for goods or services that were billed twice.

Additionally, the Federal Trade Commission Act (15 U.S.C. § 45) prohibits unfair or deceptive trade practices. Charging a customer twice for a single transaction constitutes an unfair practice that causes consumer injury.

Under Washington State's Consumer Protection Act (RCW 19.86), businesses must refund overpayments promptly. Failure to do so may result in treble damages and attorney fees.

EXPECTED RESOLUTION:

Based on these laws, Amazon is legally required to:
1. Immediately credit my account for the duplicate charge of $187.42
2. Provide written confirmation of the refund within 7 business days
3. Ensure no additional fees or interest accrue during the investigation

I have attached screenshots of both charges and the order confirmation showing only one order was placed.

If this matter is not resolved within 7 business days, I will exercise my rights under 15 U.S.C. § 1666 to withhold payment pending investigation and will file a formal complaint with the FTC and initiate a chargeback with my bank.

I expect prompt resolution of this clear billing error.

Sincerely,
Sarah M.
Order #114-9876543-9876543

→ Result: Full $187.42 refunded in 4 days`,
    result: 'Full $187.42 refunded in 4 days',
    laws: ['Fair Credit Billing Act 15 U.S.C. § 1666', 'FTC Act 15 U.S.C. § 45', 'WA Consumer Protection Act RCW 19.86']
  },
  {
    id: 2,
    company: 'Delta Airlines',
    companyIcon: '✈️',
    headline: 'Got $2,800 back from Delta',
    amount: 2800,
    daysToRefund: 11,
    preview: `Dear Delta Air Lines Customer Relations,

Re: Compensation Claim for Delayed Baggage – Flight DL1482, October 29, 2025

I am filing a formal claim for $2,800 in proven damages resulting from your 72-hour delay in delivering my checked baggage on Flight DL1482 from Atlanta to San Francisco.

LEGAL BASIS FOR CLAIM:

Under the Montreal Convention Article 19, which has the force of law in the United States (49 U.S.C. § 40105), airlines are strictly liable for damages caused by delay in the carriage of baggage...`,
    fullLetter: `Dear Delta Air Lines Customer Relations,

Re: Compensation Claim for Delayed Baggage – Flight DL1482, October 29, 2025

I am filing a formal claim for $2,800 in proven damages resulting from your 72-hour delay in delivering my checked baggage on Flight DL1482 from Atlanta to San Francisco.

LEGAL BASIS FOR CLAIM:

Under the Montreal Convention Article 19, which has the force of law in the United States (49 U.S.C. § 40105), airlines are strictly liable for damages caused by delay in the carriage of baggage. The Convention states: "The carrier is liable for damage occasioned by delay in the carriage by air of passengers, baggage or cargo."

This means Delta is legally responsible for compensating passengers for reasonable, proven expenses incurred due to baggage delay, regardless of fault. The only defenses are if Delta can prove it took "all measures that could reasonably be required to avoid the damage" or that such measures were impossible to take.

Additionally, under 14 CFR § 254.4 (U.S. Department of Transportation regulations), airlines must compensate passengers for provable direct or consequential damages resulting from delayed baggage delivery.

My bag contained business attire, toiletries, and electronics necessary for a three-day business conference. Due to the 72-hour delay, I incurred:
- Emergency clothing purchases: $1,200 (receipts attached)
- Toiletries and essentials: $180 (receipts attached)
- Replacement electronics chargers: $420 (receipts attached)
- Lost productivity from missed first-day presentations: $1,000 (documented)
Total proven damages: $2,800

EXPECTED RESOLUTION:

Based on the Montreal Convention and DOT regulations, Delta is legally obligated to:
1. Reimburse me $2,800 for documented expenses within 14 days
2. Provide written confirmation of payment
3. Compensate me for the inconvenience and breach of contract

If Delta fails to provide compensation within 14 days, I will:
- File a formal complaint with the U.S. Department of Transportation
- Pursue legal action in small claims court
- Report this matter to the Better Business Bureau

I have attached all receipts, the baggage claim ticket, and documentation of losses.

This is a straightforward liability claim under international law. I expect prompt payment.

Sincerely,
Michael R.
Confirmation #DL1482-10292025
SkyMiles #8472931

→ Result: $2,800 paid in 11 days`,
    result: '$2,800 paid in 11 days',
    laws: ['Montreal Convention Article 19', '14 CFR § 254.4', '49 U.S.C. § 40105']
  },
  {
    id: 3,
    company: 'Planet Fitness',
    companyIcon: '💪',
    headline: 'Stopped gym charges + $30 refund',
    amount: 30,
    daysToRefund: 3,
    preview: `To: Planet Fitness Corporate Billing Department

Re: Unauthorized Charges After Confirmed Cancellation – Member #PF987654

I am writing to dispute $30 in unauthorized charges that occurred after I cancelled my membership on November 1, 2025 via certified mail (USPS Tracking #9400-1234-5678).

LEGAL BASIS FOR DISPUTE:

Under the Electronic Fund Transfer Act (15 U.S.C. § 1693e), consumers have the right to stop preauthorized payments by notifying the merchant at least three business days before the scheduled transfer...`,
    fullLetter: `To: Planet Fitness Corporate Billing Department

Re: Unauthorized Charges After Confirmed Cancellation – Member #PF987654

I am writing to dispute $30 in unauthorized charges that occurred after I cancelled my membership on November 1, 2025 via certified mail (USPS Tracking #9400-1234-5678).

LEGAL BASIS FOR DISPUTE:

Under the Electronic Fund Transfer Act (15 U.S.C. § 1693e), consumers have the right to stop preauthorized payments by notifying the merchant at least three business days before the scheduled transfer. I provided proper written notice on November 1, yet charges continued on November 15, December 1, and December 15.

This also violates the Federal Trade Commission's Negative Option Rule (16 CFR Part 425), which requires businesses to:
1. Provide simple cancellation mechanisms
2. Stop charging immediately after receiving cancellation notice
3. Not continue billing after acknowledged cancellation

Furthermore, your membership agreement states cancellations are effective within 10 business days of receipt. My cancellation was acknowledged by your staff on November 5, 2025 (email confirmation attached), yet I was charged three additional times:
- November 15: $10
- December 1: $10  
- December 15: $10
Total unauthorized charges: $30

EXPECTED RESOLUTION:

Based on federal law and your own contract terms, Planet Fitness must:
1. Immediately cease all future charges to my account
2. Refund all $30 in post-cancellation charges within 7 days
3. Provide written confirmation that my membership is cancelled (retroactive to November 1, 2025)
4. Confirm no negative reporting to credit bureaus

Under the EFTA (15 U.S.C. § 1693m), consumers who successfully dispute unauthorized electronic fund transfers are entitled to full reimbursement. Additionally, under Regulation E (12 CFR § 1005.10), I have already notified my bank to stop these payments, which may result in returned payment fees that Planet Fitness will be liable for.

If this is not resolved within 7 days, I will:
- File formal complaints with the FTC and Consumer Financial Protection Bureau
- Report to my state Attorney General (consumer fraud division)
- Pursue treble damages under state consumer protection laws
- Post detailed reviews on consumer protection sites

I cancelled in good faith and provided proper notice. Your continued billing is illegal.

Cease all charges and process the refund immediately.

Regards,
Jessica L.
Member #PF987654
Cancellation Date: November 1, 2025
USPS Tracking: 9400-1234-5678

→ Result: Cancelled + $30 refunded in 3 days`,
    result: 'Cancelled + $30 refunded in 3 days',
    laws: ['Electronic Fund Transfer Act 15 U.S.C. § 1693e', 'FTC Negative Option Rule 16 CFR 425', 'Regulation E 12 CFR § 1005.10']
  },
  {
    id: 4,
    company: 'Verizon',
    companyIcon: '📱',
    headline: 'Got $142.87 Verizon credit',
    amount: 142.87,
    daysToRefund: 7,
    preview: `Dear Verizon Wireless Billing Department,

Re: Formal Billing Dispute – Duplicate Equipment Charge
Account #555-0192

I am formally disputing a duplicate equipment charge of $142.87 that appeared on both my October and November 2025 billing statements, despite only one device being added to my account.

LEGAL BASIS FOR DISPUTE:

Under the Truth in Billing provisions of the Telecommunications Act (47 U.S.C. § 201(b)), telecommunications carriers must provide service at "just and reasonable" rates and may not charge for services not provided...`,
    fullLetter: `Dear Verizon Wireless Billing Department,

Re: Formal Billing Dispute – Duplicate Equipment Charge
Account #555-0192

I am formally disputing a duplicate equipment charge of $142.87 that appeared on both my October and November 2025 billing statements, despite only one device being added to my account.

LEGAL BASIS FOR DISPUTE:

Under the Truth in Billing provisions of the Telecommunications Act (47 U.S.C. § 201(b)), telecommunications carriers must provide service at "just and reasonable" rates and may not charge for services not provided. Charging twice for the same equipment violates this statute's prohibition against unjust charges.

Additionally, FCC Truth-in-Billing rules (47 CFR § 64.2400) require that:
- Bills must be clear, plain language descriptions of charges
- Charges must be accurate and not misleading
- Customers must only be billed for services they authorize

The FCC has repeatedly held that duplicate billing constitutes a violation of these rules and requires immediate correction and refund.

Furthermore, under the Fair Credit Billing Act (15 U.S.C. § 1666), I have the right to dispute billing errors, including charges for services not accepted or not delivered as agreed. Section 1666(b)(3) requires creditors to correct billing errors promptly.

FACTS:

On September 15, 2025, I added one iPhone 15 Pro to my account (Order #VZ-847392). I was informed the equipment charge would be $142.87, split over 24 months. However, I have been charged $142.87 in both October and November statements, totaling $285.74 for a single device.

I have contacted customer service three times (October 12, October 28, November 15) and was told "the system will correct it automatically," but the duplicate charge remains on my November statement.

EXPECTED RESOLUTION:

Based on federal telecommunications law and FCC regulations, Verizon must:
1. Immediately credit my account $142.87 (the duplicate November charge)
2. Confirm the equipment charge will be $142.87 total (not per month)
3. Remove any late fees or service interruption threats related to this disputed charge
4. Provide written confirmation of the correction within 10 business days

Under 47 U.S.C. § 201(b) and FCC rules, carriers who engage in unjust billing practices may face enforcement actions. Under the Fair Credit Billing Act, I am not required to pay the disputed amount during the investigation period.

If this is not resolved within 10 business days, I will:
- File a formal complaint with the FCC (FCC Form 2000)
- Report to my state Public Utilities Commission
- Dispute the charge with my credit card company
- Request an investigation into whether this is a systematic billing error affecting other customers

I have attached copies of both October and November statements showing the duplicate $142.87 charges, as well as the original order confirmation showing only one device was purchased.

This is a clear billing error that must be corrected immediately under federal law.

Sincerely,
David K.
Account #555-0192
Order #VZ-847392

→ Result: $142.87 credited + apology email`,
    result: '$142.87 credited + apology email in 7 days',
    laws: ['Telecommunications Act 47 U.S.C. § 201(b)', 'FCC Truth-in-Billing 47 CFR § 64.2400', 'Fair Credit Billing Act 15 U.S.C. § 1666']
  }
];

export default function ExampleLetterCarousel() {
  const [selectedLetter, setSelectedLetter] = useState(null);

  return (
    <>
      <Box 
        sx={{ 
          py: 8, 
          bgcolor: '#0f172a',
          borderTop: '1px solid rgba(0, 229, 255, 0.2)',
          borderBottom: '1px solid rgba(0, 229, 255, 0.2)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #00e5ff, transparent)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #00e5ff, transparent)',
          }
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #00e5ff 0%, #00a8cc 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 40px rgba(0, 229, 255, 0.3)',
                filter: 'drop-shadow(0 0 20px rgba(0, 229, 255, 0.4))'
              }}
            >
              Real Letters That Got Results
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#94a3b8',
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400
              }}
            >
              These are actual letters generated for users — no templates, no BS.
            </Typography>
          </Box>

          {/* Letter Cards Grid */}
          <Grid container spacing={3}>
            {exampleLetters.map((letter) => (
              <Grid item xs={12} sm={6} md={3} key={letter.id}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: '#1e293b',
                    border: '1px solid rgba(0, 229, 255, 0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      border: '1px solid rgba(0, 229, 255, 0.4)',
                      boxShadow: '0 8px 32px rgba(0, 229, 255, 0.2)',
                      '& .letter-preview': {
                        maxHeight: '200px',
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #00e5ff, #00a8cc)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::before': {
                      opacity: 1,
                    }
                  }}
                  onClick={() => setSelectedLetter(letter)}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Company Icon */}
                    <Box sx={{ fontSize: '3rem', mb: 2, textAlign: 'center' }}>
                      {letter.companyIcon}
                    </Box>

                    {/* Headline */}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#00e5ff',
                        fontWeight: 600,
                        mb: 2,
                        textAlign: 'center',
                        fontSize: '1.1rem'
                      }}
                    >
                      {letter.headline}
                    </Typography>

                    {/* Success Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                        label={`${letter.daysToRefund} days`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(34, 197, 94, 0.1)',
                          color: '#22c55e',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>

                    {/* Letter Preview */}
                    <Box
                      className="letter-preview"
                      sx={{
                        position: 'relative',
                        maxHeight: '120px',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease',
                        mb: 2
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#cbd5e1',
                          fontSize: '0.85rem',
                          lineHeight: 1.6,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {letter.preview}
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '60px',
                          background: 'linear-gradient(to bottom, transparent, #1e293b)',
                        }}
                      />
                    </Box>

                    {/* See Full Letter Button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        color: '#00e5ff',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#00e5ff',
                          bgcolor: 'rgba(0, 229, 255, 0.1)',
                        }
                      }}
                    >
                      See full letter →
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Trust Indicator */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
              💡 All letters generated in under 30 seconds with Grok AI
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Letter Modal */}
      {selectedLetter && (
        <ExampleLetterModal
          letter={selectedLetter}
          open={Boolean(selectedLetter)}
          onClose={() => setSelectedLetter(null)}
        />
      )}
    </>
  );
}
